import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  api,
  ApiError,
  fetchMe,
  getApiToken,
  getStoredUser,
  login as apiLogin,
  logout as apiLogout,
  setStoredUser,
  type AuthUser,
  type HealthResponse,
} from "./lib/api";
import { matchRoute, navigate, usePath } from "./lib/router";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Login } from "./pages/Login";
import { AgentHub } from "./pages/AgentHub";
import { AgentCommissions } from "./pages/AgentCommissions";
import { AgentPayouts } from "./pages/AgentPayouts";
import { AgentLinks } from "./pages/AgentLinks";
import { AgentTeam } from "./pages/AgentTeam";
import { AgentCustomers } from "./pages/AgentCustomers";
// AgentStats lazy — bringt recharts mit (large) und ist nicht initial nötig.
// EAGER war es ein Showstopper bei React 19 wegen recharts CJS/ESM-mismatch.
import { AgentAccount } from "./pages/AgentAccount";
import { JoinPage } from "./pages/JoinPage";
import { AGBPage, DatenschutzPage } from "./pages/LegalPages";
import { PublicLandingPage } from "./pages/PublicLandingPage";
import { ChatWidget } from "./components/ChatWidget";
import { TourProvider } from "./components/PortalTour";
import { PwaInstallPrompt } from "./components/PwaInstallPrompt";
import { UpdateBanner } from "./components/UpdateBanner";
import { MobileBottomNav } from "./components/MobileBottomNav";

// Lazy-geladene Routes (selten besucht oder gross) — separates JS-Chunk
const AdminHub = lazy(() => import("./pages/AdminHub").then((m) => ({ default: m.AdminHub })));
const AdminAgentList = lazy(() => import("./pages/AdminAgentList").then((m) => ({ default: m.AdminAgentList })));
const AdminAgentNew = lazy(() => import("./pages/AdminAgentNew").then((m) => ({ default: m.AdminAgentNew })));
const AdminAgentDetail = lazy(() => import("./pages/AdminAgentDetail").then((m) => ({ default: m.AdminAgentDetail })));
const AdminCommissions = lazy(() => import("./pages/AdminCommissions").then((m) => ({ default: m.AdminCommissions })));
const AdminPayouts = lazy(() => import("./pages/AdminPayouts").then((m) => ({ default: m.AdminPayouts })));
const AdminKYC = lazy(() => import("./pages/AdminKYC").then((m) => ({ default: m.AdminKYC })));
const AdminCommissionRules = lazy(() => import("./pages/AdminCommissionRules").then((m) => ({ default: m.AdminCommissionRules })));
const AdminSettings = lazy(() => import("./pages/AdminSettings").then((m) => ({ default: m.AdminSettings })));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog").then((m) => ({ default: m.AdminAuditLog })));
const AdminActivity = lazy(() => import("./pages/AdminActivity").then((m) => ({ default: m.AdminActivity })));
const AdminCatalogBlocks = lazy(() => import("./pages/AdminCatalogBlocks").then((m) => ({ default: m.AdminCatalogBlocks })));
const AdminWebhookHealth = lazy(() => import("./pages/AdminWebhookHealth").then((m) => ({ default: m.AdminWebhookHealth })));
const AdminAttributions = lazy(() => import("./pages/AdminAttributions").then((m) => ({ default: m.AdminAttributions })));
const AdminShopSync = lazy(() => import("./pages/AdminShopSync").then((m) => ({ default: m.AdminShopSync })));
const AdminIdeen = lazy(() => import("./pages/AdminIdeen").then((m) => ({ default: m.AdminIdeen })));
const AdminErrors = lazy(() => import("./pages/AdminErrors").then((m) => ({ default: m.AdminErrors })));
const AdminCustomerOrders = lazy(() => import("./pages/AdminCustomerOrders").then((m) => ({ default: m.AdminCustomerOrders })));
const AdminCustomerOrderDetail = lazy(() => import("./pages/AdminCustomerOrderDetail").then((m) => ({ default: m.AdminCustomerOrderDetail })));
const AgentPageEditor = lazy(() => import("./pages/AgentPageEditor").then((m) => ({ default: m.AgentPageEditor })));
const AgentUpgrade = lazy(() => import("./pages/AgentUpgrade").then((m) => ({ default: m.AgentUpgrade })));
const AgentVorlagen = lazy(() => import("./pages/AgentVorlagen").then((m) => ({ default: m.AgentVorlagen })));
const AgentCustomerDirectory = lazy(() => import("./pages/AgentCustomerDirectory").then((m) => ({ default: m.AgentCustomerDirectory })));
const AgentRechnungscheck = lazy(() => import("./pages/AgentRechnungscheck").then((m) => ({ default: m.AgentRechnungscheck })));
const AgentProspekte = lazy(() => import("./pages/AgentProspekte").then((m) => ({ default: m.AgentProspekte })));
const HelpPage = lazy(() => import("./pages/HelpPage").then((m) => ({ default: m.HelpPage })));
const AgentStats = lazy(() => import("./pages/AgentStats").then((m) => ({ default: m.AgentStats })));

function LazyFallback() {
  return (
    <main className="min-h-screen grid place-items-center">
      <div className="text-sm text-ink-muted">Lade…</div>
    </main>
  );
}

// Subdomain-Routes lazy (gehören nicht ins Agent-Hub-Bundle)
const LazyPDP = lazy(() =>
  import("./pages/PublicProductDetailPage").then((m) => ({
    default: m.PublicProductDetailPage,
  })),
);
const LazyCustomerLogin = lazy(() =>
  import("./pages/PublicCustomerLogin").then((m) => ({
    default: m.PublicCustomerLogin,
  })),
);
const LazyCustomerRegister = lazy(() =>
  import("./pages/PublicCustomerRegister").then((m) => ({
    default: m.PublicCustomerRegister,
  })),
);
const LazyCustomerAccount = lazy(() =>
  import("./pages/PublicCustomerAccount").then((m) => ({
    default: m.PublicCustomerAccount,
  })),
);
const LazyCustomerCheckout = lazy(() =>
  import("./pages/PublicCustomerCheckout").then((m) => ({
    default: m.PublicCustomerCheckout,
  })),
);
const LazyCustomerOrderConfirmation = lazy(() =>
  import("./pages/PublicCustomerOrderConfirmation").then((m) => ({
    default: m.PublicCustomerOrderConfirmation,
  })),
);

function renderPublicSubdomain(agentCode: string, path: string) {
  const pdpMatch = path.match(/^\/p\/(\d+)\/?$/);
  if (pdpMatch) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <LazyPDP agentCode={agentCode} productId={pdpMatch[1]} />
      </Suspense>
    );
  }
  if (path === "/login" || path.startsWith("/login?")) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <LazyCustomerLogin agentCode={agentCode} />
      </Suspense>
    );
  }
  if (path === "/registrieren" || path.startsWith("/registrieren?")) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <LazyCustomerRegister agentCode={agentCode} />
      </Suspense>
    );
  }
  if (path === "/konto" || path.startsWith("/konto?")) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <LazyCustomerAccount agentCode={agentCode} />
      </Suspense>
    );
  }
  if (path === "/checkout" || path.startsWith("/checkout?")) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <LazyCustomerCheckout agentCode={agentCode} />
      </Suspense>
    );
  }
  const orderMatch = path.match(/^\/bestellung\/([A-Z0-9-]+)\/?$/i);
  if (orderMatch) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <LazyCustomerOrderConfirmation
          agentCode={agentCode}
          orderNumber={orderMatch[1].toUpperCase()}
        />
      </Suspense>
    );
  }
  return <PublicLandingPage agentCode={agentCode} />;
}

function CustomDomainResolver({ host, path }: { host: string; path: string }) {
  const [state, setState] = useState<{ kind: "loading" | "found" | "notfound"; code?: string }>({
    kind: "loading",
  });
  useEffect(() => {
    let cancelled = false;
    resolvePublicDomain(host)
      .then((res) => {
        if (!cancelled) setState({ kind: "found", code: res.agent_code });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "notfound" });
      });
    return () => {
      cancelled = true;
    };
  }, [host]);

  if (state.kind === "loading") {
    return (
      <main className="min-h-screen grid place-items-center bg-cream">
        <div className="text-sm text-ink-muted">Lade…</div>
      </main>
    );
  }
  if (state.kind === "notfound") {
    return (
      <main className="min-h-screen grid place-items-center bg-cream px-4">
        <div className="text-center">
          <div className="text-4xl mb-2">🌐</div>
          <h1 className="text-xl font-semibold tracking-tight">Diese Domain ist nicht verknüpft</h1>
          <p className="mt-2 text-sm text-ink-muted">
            <code className="font-mono">{host}</code> zeigt zwar auf unsere Server, ist aber keinem
            DP-Connect-Agent zugeordnet.
          </p>
          <a
            href="https://dpconnect.de"
            className="mt-4 inline-block text-sm text-accent-orange hover:underline"
          >
            Zum DP-Connect-Shop →
          </a>
        </div>
      </main>
    );
  }
  return renderPublicSubdomain(state.code!, path);
}

import type { JoinResponse } from "./lib/public";
import { resolvePublicDomain } from "./lib/page";

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [bootstrapping, setBootstrapping] = useState<boolean>(() => Boolean(getApiToken()));
  const path = usePath();

  useEffect(() => {
    if (!getApiToken()) {
      setBootstrapping(false);
      return;
    }
    fetchMe()
      .then((fresh) => {
        setStoredUser(fresh);
        setUser(fresh);
        // WP-SSO: bei App-Mount versuchen, dass der Agent auch auf
        // dpconnect.de eingeloggt ist. Debounced auf 30min in wp_sso.ts.
        if (fresh.role === "agent") {
          import("./lib/wp_sso").then((m) => m.triggerWpSso().catch(() => {}));
        }
      })
      .catch((err) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          apiLogout();
          setUser(null);
        }
      })
      .finally(() => setBootstrapping(false));
  }, []);

  const { data: health } = useQuery<HealthResponse>({
    queryKey: ["health"],
    queryFn: () => api.get<HealthResponse>("/health"),
    enabled: Boolean(user),
    refetchInterval: 60_000,
  });

  const handleLogin = useCallback(async (email: string, password: string) => {
    const fresh = await apiLogin(email, password);
    setUser(fresh);
    // WP-SSO direkt nach erfolgreichem Login triggern (force, ignoriert Cache)
    if (fresh.role === "agent") {
      import("./lib/wp_sso").then((m) => m.triggerWpSso(true).catch(() => {}));
    }
  }, []);

  const handleLogout = useCallback(() => {
    apiLogout();
    // WP-SSO-Cache leeren — WP-Cookie bleibt bewusst aktiv (User-Wunsch)
    import("./lib/wp_sso").then((m) => m.clearWpSsoCache());
    setUser(null);
    navigate("/");
  }, []);

  const handleJoined = useCallback((response: JoinResponse) => {
    setUser(response.user);
    if (response.user.role === "agent") {
      import("./lib/wp_sso").then((m) => m.triggerWpSso(true).catch(() => {}));
    }
    navigate("/links");
  }, []);

  if (bootstrapping) {
    return (
      <main className="min-h-screen gradient-radial-warm grid place-items-center">
        <div className="text-sm text-ink-muted">Lade…</div>
      </main>
    );
  }

  // Subdomain-Routing: {agent_code}.dpconnect.de → Public Landing-Page (oder PDP)
  // agenten.dpconnect.de und localhost laufen normal weiter
  const host = window.location.hostname;
  const subdomainMatch = host.match(/^([a-z0-9-]+)\.dpconnect\.de$/i);
  const isOurDomain =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "agenten.dpconnect.de" ||
    host.endsWith(".dpconnect.de");

  // Custom-Domain: weder agenten.dpconnect.de noch lokal — versuche resolve
  if (!isOurDomain) {
    return <CustomDomainResolver host={host} path={path} />;
  }

  if (subdomainMatch && subdomainMatch[1].toLowerCase() !== "agenten") {
    const code = subdomainMatch[1].toLowerCase();
    return renderPublicSubdomain(code, path);
  }

  // Public /join Route: kein Auth nötig — auch /join/vorname-nachname (Team-Links)
  if (path === "/join" || path.startsWith("/join?") || path.startsWith("/join/")) {
    if (user) {
      navigate("/links");
      return null;
    }
    return <JoinPage onJoined={handleJoined} />;
  }

  // Public Legal-Pages: kein Auth nötig
  if (path === "/agb" || path === "/agb/") return <AGBPage />;
  if (path === "/datenschutz" || path === "/datenschutz/") return <DatenschutzPage />;

  // Public Landing-Page eines Agenten: kein Auth nötig
  const landingMatch = path.match(/^\/p\/([^/]+)\/?$/);
  if (landingMatch) {
    return <PublicLandingPage agentCode={decodeURIComponent(landingMatch[1])} />;
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const route = matchRoute(path);
  const isAdminRoute = path.startsWith("/admin");
  const canAccessAdmin = user.role === "admin";

  const content = (
    <div className="min-h-screen flex flex-col">
      <SupportModeBanner />
      <Header user={user} onLogout={handleLogout} />

      <Suspense fallback={<LazyFallback />}>
        {renderRoute(route, user, canAccessAdmin)}
      </Suspense>

      {canAccessAdmin && (
        <div className="mx-auto max-w-6xl w-full px-4 pb-4">
          <div className="rounded-xl border border-line bg-cream-soft/40 px-3 py-2 text-xs text-ink-muted flex gap-3">
            <a
              href={isAdminRoute ? "/" : "/admin"}
              onClick={(e) => {
                e.preventDefault();
                navigate(isAdminRoute ? "/" : "/admin");
              }}
              className="text-accent-orange hover:underline font-medium"
            >
              {isAdminRoute ? "→ Agenten-Ansicht" : "→ Admin-Ansicht"}
            </a>
            <span className="ml-auto">Admin · {user.email}</span>
          </div>
        </div>
      )}

      <div className="mt-auto">
        <Footer backendVersion={health?.build_version} />
      </div>

      {/* Chat-Widget — nur für Agenten, nicht für Admins, nicht im Admin-Bereich */}
      {user.role === "agent" && !isAdminRoute && <ChatWidget />}

      {/* Sticky Bottom-Nav fuer Agenten auf Mobile */}
      {user.role === "agent" && !isAdminRoute && <MobileBottomNav />}

      {/* PWA-Install-Prompt — alle eingeloggten User */}
      <PwaInstallPrompt />

      {/* Update-Banner — zeigt sich wenn neue Version verfuegbar */}
      <UpdateBanner backendVersion={health?.build_version} />
    </div>
  );

  // TourProvider nur für Agenten, nicht im Admin-Bereich
  if (user.role === "agent" && !isAdminRoute) {
    return <TourProvider user={user}>{content}</TourProvider>;
  }
  return content;
}

/**
 * Support-Modus: Admin ist als Agent eingeloggt (Impersonation).
 * Sichtbar sobald eine gesicherte Admin-Session im localStorage liegt —
 * ein Klick stellt sie wieder her.
 */
function SupportModeBanner() {
  const backup = localStorage.getItem("dp-agenten-support-backup-token");
  if (!backup) return null;

  let agentName = "Agent";
  try {
    const u = JSON.parse(localStorage.getItem("dp-agenten-user") || "{}");
    agentName = u.name || u.agent_code || "Agent";
  } catch {
    /* egal */
  }

  function backToAdmin() {
    const token = localStorage.getItem("dp-agenten-support-backup-token");
    const userJson = localStorage.getItem("dp-agenten-support-backup-user");
    if (token) localStorage.setItem("dp-agenten-token", token);
    if (userJson) localStorage.setItem("dp-agenten-user", userJson);
    localStorage.removeItem("dp-agenten-support-backup-token");
    localStorage.removeItem("dp-agenten-support-backup-user");
    window.location.href = "/admin/agents";
  }

  return (
    <div className="sticky top-0 z-50 bg-amber-400 text-amber-950 px-4 py-2 text-sm flex items-center justify-between gap-3 shadow">
      <span className="font-medium truncate">
        🛠 Support-Modus — du siehst das Portal als <strong>{agentName}</strong>
      </span>
      <button
        type="button"
        onClick={backToAdmin}
        className="shrink-0 rounded-lg bg-amber-950 text-amber-50 px-3 py-1 text-xs font-semibold hover:bg-amber-900"
      >
        Zurück zum Admin
      </button>
    </div>
  );
}

function renderRoute(
  route: ReturnType<typeof matchRoute>,
  user: AuthUser,
  canAccessAdmin: boolean,
) {
  // Admin-Routen ohne Admin-Rolle → fallback auf Agent-Hub
  if (route.kind.startsWith("admin") && !canAccessAdmin) {
    return <AgentHub user={user} />;
  }

  switch (route.kind) {
    case "agent-hub":
      return <AgentHub user={user} />;
    case "agent-commissions":
      return <AgentCommissions />;
    case "agent-payouts":
      return <AgentPayouts />;
    case "agent-links":
      return <AgentLinks />;
    case "agent-team":
      return <AgentTeam />;
    case "agent-customers":
      return <AgentCustomers />;
    case "agent-upgrade":
      return <AgentUpgrade />;
    case "agent-stats":
      return <AgentStats />;
    case "agent-account":
      return <AgentAccount />;
    case "agent-page-editor":
      return <AgentPageEditor />;
    case "agent-help":
      return <HelpPage />;
    case "agent-vorlagen":
      return <AgentVorlagen />;
    case "agent-customer-directory":
      return <AgentCustomerDirectory />;
    case "agent-rechnungscheck":
      return <AgentRechnungscheck />;
    case "agent-prospekte":
      return <AgentProspekte />;
    case "public-join":
    case "public-agb":
    case "public-datenschutz":
    case "public-landing-page":
      // Werden oben (vor Auth-Check) abgefangen — Safety-Fallback
      return <AgentHub user={user} />;
    case "admin-hub":
      return <AdminHub />;
    case "admin-agents-list":
      return <AdminAgentList />;
    case "admin-agents-new":
      return <AdminAgentNew />;
    case "admin-agent-detail":
      return <AdminAgentDetail agentId={route.agentId} />;
    case "admin-commissions":
      return <AdminCommissions />;
    case "admin-payouts":
      return <AdminPayouts />;
    case "admin-kyc":
      return <AdminKYC />;
    case "admin-commission-rules":
      return <AdminCommissionRules />;
    case "admin-settings":
      return <AdminSettings />;
    case "admin-audit-log":
      return <AdminAuditLog />;
    case "admin-activity":
      return <AdminActivity />;
    case "admin-catalog-blocks":
      return <AdminCatalogBlocks />;
    case "admin-webhook-health":
      return <AdminWebhookHealth />;
    case "admin-attributions":
      return <AdminAttributions />;
    case "admin-shop-sync":
      return <AdminShopSync />;
    case "admin-ideen":
      return <AdminIdeen />;
    case "admin-errors":
      return <AdminErrors />;
    case "admin-customer-orders":
      return <AdminCustomerOrders />;
    case "admin-customer-order-detail":
      return <AdminCustomerOrderDetail orderId={route.orderId} />;
    case "not-found":
      return (
        <main className="mx-auto max-w-3xl px-4 py-16 text-center">
          <h2 className="text-xl font-semibold mb-2">Seite nicht gefunden</h2>
          <p className="text-sm text-ink-muted">
            Die URL <code className="font-mono">{window.location.pathname}</code> kennt das System nicht.
          </p>
        </main>
      );
  }
}
